import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import BasicModal from "@/components/basic-modal";
import UITextField from "@/components/ui-controls/textfield";
import { useSelector, useDispatch } from "react-redux";
import { getAllRequests } from "@/store/slices/requests";
import { clearWorkflowSnapshot } from "@/store/slices/workflow/workflowSnapshotSlice";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import guid from "guid";
import { useRouter } from "next/router";
import { usePathname } from "next/navigation";
import { toast } from "react-toastify";
import OnInitHub from "@/services/signal-R/on-receive-hook";
import { Alert } from "@mui/material";
import {
  LimitValidationMsgs,
  ToastTemplate,
  charactersLimit,
} from "@/services/variables";
import WF_signalr from "../workflow-signalr-constants";
import { AllowOnlyAlphaNumaricSpace_Validation } from "@/services/generic.methods";

const nameLimit = 200;

const CreateWorkflowNameModal = ({
  modalTitle = "Create Automation",
  openModal,
  handleClose,
  onSaveCallback = null,
  parentLoading = false,
  parentError = "",
  savedName = "",
  saveButtonTitle = "Next",
  openTemplateModal,
  redirect = false,
}) => {
  const {
    hubName,
    InvokeMethodNames: { createWorkflowName },
    onReceiveMethodNames: { onCreateWorkflowName },
  } = WF_signalr;
  const { clearStateByMethods } = OnInitHub();
  const [name, setName] = useState(savedName);
  const [error, setError] = useState("");
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const { statuses, RTC } = useSignalRClient({ hubName });
  const WFhub = RTC[hubName];
  const WFhubStatus = statuses[hubName];
  const requests = useSelector(getAllRequests);
  const dispatch = useDispatch();
  const router = useRouter();
  const pathName = usePathname();
  const [time, setTime] = useState(null);

  useEffect(() => {
    //response method listeners
    if (requests[onCreateWorkflowName]) {
      let response = requests[onCreateWorkflowName];
      console.log("response---", response);
      // clear timeout if listner works
      clearTimeout(time);
      setTime(null);
      if (response.isSuccess) {
        setLoading(false);
        handleClose();
        setName("");
        setError("");
        setSaveBtnDisabled(false);
        console.log("redirect");
        toast.success(
          ToastTemplate.success("Automation Created Successfully.")
        );
        if (openModal) {
          if (redirect) {
            dispatch(clearWorkflowSnapshot());
            router.push(`/crm/automation/automations/create?id=${response.id}`);
          } else {
            openTemplateModal(response.id);
          }
        }
      } else {
        setLoading(false);
        setError(response.errors[0]);
        setSaveBtnDisabled(true);
        // toast.error(ToastTemplate.error(response.errors[0] || ""));
      }
      clearStateByMethods([onCreateWorkflowName]);
    }
  }, [requests[onCreateWorkflowName]]);

  useEffect(() => {
    setSaveBtnDisabled(!name.trim() || name.length > nameLimit);
  }, [name]);

  const saveWorkflowName = () => {
    if (onSaveCallback) {
      onSaveCallback(name);
    } else {
      let method = createWorkflowName;
      setLoading(true);
      if (WFhub && statuses[hubName]) {
        let payload = {
          name: name,
          requestId: guid.raw(),
        };

        WFhub.invoke(method, payload).then((res) => {
          console.log("on success sent", res);
          setTime(setTimeout(resetModalAfter5s, 30000));
        });
      }
    }
  };

  const resetModalAfter5s = () => {
    toast.error(
      ToastTemplate.error("Something went wrong. Please try again later.")
    );
    setName("");
    setLoading(false);
    handleClose();
    setError("");
    setSaveBtnDisabled(false);
  };

  const handleOnChange = (e) => {
    const value = e?.target?.value;

    if (!AllowOnlyAlphaNumaricSpace_Validation(value)) return;

    if (saveBtnDisabled && value != name) {
      setSaveBtnDisabled(false);
    }

    setName(e.target.value);
  };
  return (
    <BasicModal
      open={openModal}
      title={modalTitle}
      handleClose={() => {
        handleClose();
        setError("");
        setName("");
      }}
      cancelBtnHidden={true}
      saveButtonTitle={saveButtonTitle}
      saveBtnLoading={loading || parentLoading}
      saveBtnDisabled={saveBtnDisabled}
      saveCallback={saveWorkflowName}
      modalBodySx={{ padding: "1.2rem 1rem" }}
      size="xs"
    >
      <Box sx={{ p: 0, m: 0, width: "100%" }}>
        {(error || parentError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || parentError}
          </Alert>
        )}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <UITextField
            name={"name"}
            value={name || ""}
            title={"Automation Name"}
            onChange={handleOnChange}
            error={
              name.length > charactersLimit.title
                ? LimitValidationMsgs(charactersLimit.title)
                : ""
            }
          />
        </FormControl>
      </Box>
    </BasicModal>
  );
};

export default CreateWorkflowNameModal;
